/**
 * AdminLayout — navigation wrapper for all admin pages.
 *
 * Responsibilities:
 *  - Auth guard: listens to Firebase Auth state; redirects to /login if the
 *    user is not signed in.
 *  - Renders the top nav bar with links to all admin sections and a logout
 *    button. Infrequently-used config/preview pages are grouped under an
 *    "Einstellungen" dropdown.
 *  - Displays the build timestamp and the git commit the bundle was built
 *    from (src/buildInfo.js) beneath the brand name, so it's always clear
 *    which version is live. A "+dirty" suffix on the commit means the
 *    build came from a working tree with uncommitted changes.
 *  - Wraps page content in a <main> with consistent background and padding.
 */
import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { BUILD_TIME, BUILD_COMMIT } from "../buildInfo";

// Pages that live inside the Einstellungen dropdown
const SETTINGS_PATHS = [
  "/preview/order-review",
  "/preview/order-emails",
  "/preview/shipping-emails",
  "/preview/delivery-emails",
  "/preview/progress-emails",
  "/preview/review",
  "/preview/emails",
  "/settings/review-ai",
  "/settings/sync",
  "/settings/invoices",
  "/test-orders",
  "/draft-review",
];

const SETTINGS_ITEMS = [
  { path: "/preview/order-review",     label: "Bestellformular-Vorschau" },
  { path: "/preview/order-emails",     label: "Bestell-E-Mails-Vorschau" },
  { path: "/preview/shipping-emails",  label: "Versand-E-Mails-Vorschau" },
  { path: "/preview/delivery-emails",  label: "Zustellung-E-Mails-Vorschau" },
  { path: "/preview/progress-emails",  label: "Zwischenbenachrichtigung" },
  { path: "/preview/review",           label: "Entwurf-Seiten-Vorschau" },
  { path: "/preview/emails",           label: "Entwurf-E-Mails-Vorschau" },
  { path: "/settings/review-ai",       label: "KI-Antwort-Einstellungen" },
  { path: "/settings/sync",            label: "Auto-Sync" },
  { path: "/settings/invoices",        label: "Rechnungen" },
  { path: "/test-orders",              label: "Test-Daten" },
  // Unterpunkt von Test-Daten: der Auto-Entwurfs-Prozess wird noch
  // erprobt und soll nicht prominent in der Hauptnavigation stehen.
  { path: "/draft-review",             label: "Auto-Entwürfe", sub: true },
];

const AdminLayout = ({ children }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) navigate("/login");
    });
    return () => unsubscribe();
  }, [navigate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Close dropdown on route change
  useEffect(() => { setDropdownOpen(false); }, [location.pathname]);

  const handleLogout = async () => {
    const auth = getAuth();
    await signOut(auth);
    navigate("/login");
  };

  const linkStyle = (path) => ({
    padding: "0.4em 1em",
    borderRadius: "6px",
    textDecoration: "none",
    fontSize: "0.9rem",
    fontWeight: "500",
    color: location.pathname === path ? "#4a2c2a" : "#4a5568",
    background: location.pathname === path ? "#f5ede8" : "transparent",
    transition: "background 0.15s, color 0.15s",
    whiteSpace: "nowrap",
  });

  const settingsActive = SETTINGS_PATHS.includes(location.pathname);

  return (
    <div style={{ minHeight: "100vh", background: process.env.REACT_APP_BG_COLOR || "#fdf7f4" }}>
      <nav style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between",
        padding: "0 2em", height: "56px",
        background: "#ffffff", borderBottom: "1px solid #e2e8f0",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
      }}>
        {/* Brand */}
        <div>
          <span style={{
            fontWeight: "700", fontSize: "1rem", color: "#1a202c",
            letterSpacing: "0.02em",
          }}>
            {process.env.REACT_APP_SHOP_NAME || "Printsonalities"} Admin
          </span>
          <span
            style={{
              display: "block", fontSize: "0.7rem", color: "#a0aec0",
              fontWeight: "400", marginTop: "1px", letterSpacing: "0.01em",
            }}
            title={BUILD_COMMIT && BUILD_COMMIT !== "unknown"
              ? `Gebaut aus Commit ${BUILD_COMMIT}`
              : "Commit unbekannt — ohne Git gebaut"}
          >
            Build: {BUILD_TIME}
            {BUILD_COMMIT && BUILD_COMMIT !== "unknown" && (
              <>
                {" · "}
                <code style={{
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  fontSize: "0.68rem",
                  color: BUILD_COMMIT.endsWith("+dirty") ? "#c05621" : "#a0aec0",
                }}>{BUILD_COMMIT}</code>
              </>
            )}
          </span>
        </div>

        {/* Nav links */}
        <div style={{ display: "flex", gap: "0.25em", alignItems: "center" }}>
          <Link to="/dashboard" style={linkStyle("/dashboard")}>Dashboard</Link>
          <Link to="/orders" style={linkStyle("/orders")}>Bestellungen</Link>
          <Link to="/order-confirmations" style={linkStyle("/order-confirmations")}>
            Bestätigungen
          </Link>
          <Link to="/" style={linkStyle("/")}>Entwurf hochladen</Link>
          <Link to="/overview" style={linkStyle("/overview")}>Entwürfe</Link>
          <Link to="/reviews" style={linkStyle("/reviews")}>Bewertungen</Link>
          <Link to="/produktion" style={linkStyle("/produktion")}>Produktion</Link>
          <Link to="/shipping" style={linkStyle("/shipping")}>Versand</Link>
          <Link to="/delivery" style={linkStyle("/delivery")}>Zustellung</Link>
          <Link to="/reporting" style={linkStyle("/reporting")}>Reporting</Link>

          {/* Einstellungen dropdown */}
          <div ref={dropdownRef} style={{ position: "relative" }}>
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              style={{
                padding: "0.4em 1em", borderRadius: "6px",
                border: "none", cursor: "pointer",
                fontSize: "0.9rem", fontWeight: "500",
                color: settingsActive ? "#4a2c2a" : "#4a5568",
                background: settingsActive
                  ? "#f5ede8"
                  : dropdownOpen ? "#f0f0f0" : "transparent",
                transition: "background 0.15s, color 0.15s",
                whiteSpace: "nowrap",
              }}
            >
              Einstellungen {dropdownOpen ? "▴" : "▾"}
            </button>

            {dropdownOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 6px)", right: 0,
                background: "#fff", borderRadius: "8px",
                boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                border: "1px solid #e2e8f0",
                minWidth: "220px", zIndex: 100,
                padding: "0.4em 0",
              }}>
                {SETTINGS_ITEMS.map(({ path, label, sub }) => (
                  <Link
                    key={path}
                    to={path}
                    style={{
                      display: "block",
                      padding: sub ? "0.4em 1.1em 0.5em 2.1em" : "0.55em 1.1em",
                      fontSize: sub ? "0.82rem" : "0.875rem",
                      fontWeight: location.pathname === path ? "700" : "500",
                      color: location.pathname === path ? "#4a2c2a" : "#4a5568",
                      background: location.pathname === path
                        ? "#fdf7f4" : "transparent",
                      textDecoration: "none",
                      transition: "background 0.1s",
                    }}
                    onMouseEnter={(e) => {
                      if (location.pathname !== path) {
                        e.currentTarget.style.background = "#f7f7f7";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background =
                        location.pathname === path ? "#fdf7f4" : "transparent";
                    }}
                  >
                    {sub && (
                      <span style={{ color: "#cbd5e0", marginRight: "0.45em" }}>
                        └
                      </span>
                    )}
                    {label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <button
          style={{
            padding: "0.4em 1em", borderRadius: "6px",
            border: "1px solid #e2e8f0", background: "transparent",
            color: "#718096", fontSize: "0.85rem",
            fontWeight: "500", cursor: "pointer",
          }}
          onClick={handleLogout}
        >
          Logout
        </button>
      </nav>

      <main style={{ padding: "2em" }}>
        {children}
      </main>
    </div>
  );
};

export default AdminLayout;
